'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Edit2, Trash2, Warehouse, MapPin } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { warehouses } from '../data/mock-data';
import { calculateWarehouseUsage } from '../types';
import type { Warehouse as WarehouseType } from '../types';

// 창고 추가 모달
function WarehouseAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="창고 추가"
      description="새로운 창고를 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="창고코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="창고명" required>
            <FormInput placeholder="창고명을 입력하세요" />
          </FormField>
          <FormField label="창고 유형" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="raw_material">원자재 창고</option>
              <option value="finished_goods">완제품 창고</option>
              <option value="wip">재공품 창고</option>
              <option value="packaging">포장재 창고</option>
              <option value="spare_parts">부품 창고</option>
            </FormSelect>
          </FormField>
          <FormField label="담당자">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="u001">김창고</option>
              <option value="u002">이물류</option>
              <option value="u003">박재고</option>
            </FormSelect>
          </FormField>
          <FormField label="용량" required>
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
          <FormField label="상태">
            <FormSelect>
              <option value="active">운영중</option>
              <option value="inactive">중단</option>
            </FormSelect>
          </FormField>
        </div>
        <FormField label="주소" required>
          <FormInput placeholder="창고 주소를 입력하세요" />
        </FormField>
        <FormField label="비고">
          <FormTextarea rows={3} placeholder="창고에 대한 추가 정보를 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function WarehouseListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return warehouses.filter((wh) => {
      // 검색 필터
      const matchesSearch =
        wh.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wh.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        wh.address.toLowerCase().includes(searchQuery.toLowerCase());

      // 활성화 필터
      const matchesActive = showInactiveOnly ? !wh.isActive : true;

      return matchesSearch && matchesActive;
    });
  }, [searchQuery, showInactiveOnly]);

  const columns = [
    {
      key: 'code',
      label: '창고코드',
      width: '10%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{value as string}</span>
      ),
    },
    {
      key: 'name',
      label: '창고명',
      width: '18%',
      sortable: true,
      render: (value: unknown, row: WarehouseType) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-accent flex items-center justify-center flex-shrink-0">
            <Warehouse className="w-4 h-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{value as string}</div>
            {row.manager && (
              <div className="text-xs text-muted-foreground truncate">
                담당: {row.manager}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'address',
      label: '주소',
      width: '22%',
      render: (value: unknown) => (
        <div className="flex items-center gap-1 text-sm">
          <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span className="truncate">{value as string}</span>
        </div>
      ),
    },
    {
      key: 'usage',
      label: '사용률',
      width: '15%',
      render: (_: unknown, row: WarehouseType) => {
        const usage = calculateWarehouseUsage(row);
        const color =
          usage >= 90
            ? 'bg-red-500'
            : usage >= 70
            ? 'bg-yellow-500'
            : 'bg-green-500';
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${color} transition-all`}
                style={{ width: `${usage}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground w-10 text-right whitespace-nowrap">
              {usage}%
            </span>
          </div>
        );
      },
    },
    {
      key: 'capacity',
      label: '용량',
      width: '12%',
      align: 'right' as const,
      render: (value: unknown, row: WarehouseType) => (
        <span className="text-sm whitespace-nowrap">
          {row.usedCapacity.toLocaleString()} / {(value as number).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'isActive',
      label: '상태',
      width: '8%',
      render: (value: unknown) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            value
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
          }`}
        >
          {value ? '운영중' : '중단'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '8%',
      render: (_: unknown, row: WarehouseType) => (
        <div className="flex items-center gap-1">
          <button
            className="p-1.5 rounded hover:bg-accent"
            title="수정"
          >
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
          <button
            className="p-1.5 rounded hover:bg-accent"
            title="삭제"
          >
            <Trash2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 요약 정보
  const totalCapacity = warehouses.reduce((sum, wh) => sum + wh.capacity, 0);
  const totalUsed = warehouses.reduce((sum, wh) => sum + wh.usedCapacity, 0);
  const activeWarehouses = warehouses.filter((wh) => wh.isActive).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="창고 관리"
        description="물류 창고를 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            창고 추가
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 창고</div>
          <div className="text-2xl font-bold mt-1">
            {warehouses.length}개
            <span className="text-sm font-normal text-muted-foreground ml-2">
              (운영중 {activeWarehouses}개)
            </span>
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 용량</div>
          <div className="text-2xl font-bold mt-1">
            {totalCapacity.toLocaleString()}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 사용률</div>
          <div className="text-2xl font-bold mt-1">
            {Math.round((totalUsed / totalCapacity) * 100)}%
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({totalUsed.toLocaleString()} 사용중)
            </span>
          </div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="창고코드, 창고명, 주소 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 비활성 창고만 보기 */}
        <label className="flex items-center gap-2 h-10 px-3 rounded-lg border border-border bg-background text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showInactiveOnly}
            onChange={(e) => setShowInactiveOnly(e.target.checked)}
            className="w-4 h-4 rounded border-border"
          />
          <span>비활성 창고만</span>
        </label>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 창고 추가 모달 */}
      <WarehouseAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
